import { useEffect } from "react";
import { initials } from "../lib/format";
import { PERM_LABEL } from "../lib/itemTypes";
import { OWNER_ORG, useVault } from "../state/store";
import { Button, Eyebrow, IconButton } from "../ui/ds";
import { useIsMobile, useWidth } from "../ui/hooks";
import { Icon } from "../ui/Icon";
import { Modals } from "./Modals";
import { ClientsView, ExpiringView } from "./Overviews";
import { GeneratorView } from "./Generator";
import { SettingsView } from "./Settings";
import { UIProvider, usePerspective, useUI, type View } from "./ui";
import { VaultView } from "./VaultView";

export function App() {
  const { data } = useVault();
  const first = data.vaults[0]?.id ?? null;
  return (
    <UIProvider initialVault={first}>
      <Shell />
    </UIProvider>
  );
}

interface NavRow {
  key: string;
  label: string;
  icon: string;
  on: boolean;
  onClick: () => void;
  count?: number | string;
  dot?: boolean;
}

function Shell() {
  const v = useVault();
  const { data, me, preview, setPreview, loaded } = v;
  const p = usePerspective();
  const { nav, go, openVault, setModal } = useUI();
  const mobile = useIsMobile();
  const narrow = useWidth() < 1180;

  // Land on the first vault once data arrives (and when the open one vanishes).
  useEffect(() => {
    if (!p.vaults.length) return;
    if (!nav.vaultId || !p.vaults.some((x) => x.id === nav.vaultId)) go({ vaultId: p.vaults[0]!.id });
  }, [p.vaults, nav.vaultId, go]);

  const vault = p.vaults.find((x) => x.id === nav.vaultId) ?? p.vaults[0] ?? null;
  const onVault = nav.view === "vault";
  const goView = (view: View) => go({ view, pane: "list" });

  const personal = p.vaults.filter((x) => x.kind === "personal");
  const clients = p.vaults.filter((x) => x.kind === "client");
  const vaultRow = (x: (typeof p.vaults)[number]): NavRow => ({
    key: x.id,
    label: x.name,
    icon: x.kind === "personal" ? "lock-keyhole" : "building-2",
    on: onVault && vault?.id === x.id,
    onClick: () => openVault(x.id),
    count: p.canSee(x) ? p.liveItems(x).length : "",
    dot: p.waiting(x) > 0,
  });

  const groups: { label: string; rows: NavRow[] }[] = p.asClient
    ? [
        { label: "", rows: p.vaults.map(vaultRow) },
        { label: "", rows: [{ key: "settings", label: "Settings", icon: "settings", on: nav.view === "settings", onClick: () => goView("settings") }] },
      ]
    : [
        { label: "", rows: personal.map(vaultRow) },
        { label: "Clients", rows: clients.map(vaultRow) },
        {
          label: "",
          rows: [
            {
              key: "expiring",
              label: "Expiring soon",
              icon: "calendar-clock",
              on: nav.view === "expiring",
              onClick: () => goView("expiring"),
              count: p.expiring.length || "",
              dot: p.expiring.some((x) => x.ex.status === "expired"),
            },
            { key: "clients", label: "All clients", icon: "users", on: nav.view === "clients", onClick: () => goView("clients") },
            { key: "gen", label: "Generator", icon: "dices", on: nav.view === "gen", onClick: () => goView("gen") },
            { key: "settings", label: "Settings", icon: "settings", on: nav.view === "settings", onClick: () => goView("settings") },
          ],
        },
      ];

  const canAddHere = vault ? p.canAdd(vault) : false;
  const newLabel = p.asClient ? "Add credential" : "New item";
  const openNew = () =>
    setModal({
      kind: "item",
      itemId: null,
      vaultId: onVault && vault ? vault.id : personal[0]?.id,
      catId: onVault && nav.cat && !nav.cat.startsWith("__") && nav.cat !== "all" ? nav.cat : undefined,
    });

  const startPreview = () => {
    const target = vault?.kind === "client" ? vault : clients[0];
    if (!target) {
      v.toast("No client vaults yet", "Create one from All clients to see what a client sees.", "info");
      return;
    }
    const m = data.members.find((x) => x.vaultId === target.id);
    setPreview({ vaultId: target.id, userId: m?.userId ?? "", name: m?.name ?? (target.contactName || "Client"), perm: m?.perm ?? "view" });
    openVault(target.id);
  };

  const showSide = !mobile || nav.view === "home";
  const view = !mobile && nav.view === "home" ? "vault" : nav.view;
  const meEmail = preview ? data.members.find((m) => m.userId === preview.userId)?.email || preview.name : me.email;
  const meName = preview ? preview.name : me.name;

  const tabs: [string, string, View][] = p.asClient
    ? [
        ["Vault", "lock-keyhole", "home"],
        ["Settings", "settings", "settings"],
      ]
    : [
        ["Vaults", "lock-keyhole", "home"],
        ["Clients", "users", "clients"],
        ["Generator", "dices", "gen"],
        ["Settings", "settings", "settings"],
      ];

  return (
    <div className={`app${mobile ? " is-mobile" : ""}${narrow ? " is-narrow" : ""}`}>
      {preview ? (
        <div className="client-bar">
          <span>
            Client view · previewing as <strong>{preview.name}</strong>, {data.vaults.find((x) => x.id === preview.vaultId)?.name} · {PERM_LABEL[preview.perm]}
          </span>
          <Button
            variant="inverse"
            size="sm"
            onClick={() => {
              const id = preview.vaultId;
              setPreview(null);
              openVault(id, { tab: "access" });
            }}
          >
            Back to your vault
          </Button>
        </div>
      ) : null}
      <div className="app-row">
        {showSide ? (
          <nav className="sidebar" aria-label="Vaults">
            <div className="brandrow side-logo">
              <img src="/agile-icon.png" alt="" />
              <span>Agile Vault</span>
            </div>
            {canAddHere || (!p.asClient && personal.length) ? (
              <div className="side-new">
                <Button fullWidth icon="plus" onClick={openNew}>
                  {newLabel}
                </Button>
              </div>
            ) : null}
            <div className="side-nav">
              {groups.map((g, gi) =>
                g.rows.length || g.label ? (
                  <div className="nav-group" key={gi}>
                    {g.label ? (
                      <div className="nav-group-label">
                        <Eyebrow size="sm" tone="muted">
                          {g.label}
                        </Eyebrow>
                      </div>
                    ) : null}
                    {g.rows.map((r) => (
                      <button key={r.key} type="button" className={`nav-row${r.on && !mobile ? " is-on" : ""}`} aria-current={r.on ? "page" : undefined} onClick={r.onClick}>
                        <Icon name={r.icon} size={16} />
                        <span className="nav-label">{r.label}</span>
                        {r.dot ? <span className="nav-dot" aria-label="Needs attention" /> : null}
                        <span className="nav-count">{r.count ?? ""}</span>
                      </button>
                    ))}
                    {g.label === "Clients" && !g.rows.length ? (
                      <button type="button" className="nav-row" onClick={() => setModal({ kind: "client" })}>
                        <Icon name="plus" size={16} />
                        <span className="nav-label muted">New client vault</span>
                      </button>
                    ) : null}
                  </div>
                ) : null,
              )}
              {loaded && !p.vaults.length ? (
                <div className="list-empty">
                  {p.asClient
                    ? `${OWNER_ORG} is finishing sharing your vault. It’ll appear here once they next open Agile Vault.`
                    : "No vaults yet."}
                </div>
              ) : null}
            </div>
            <div className="side-foot">
              <div className="avatar">{initials(meName)}</div>
              <div className="side-foot-email" title={meEmail}>
                {meEmail}
              </div>
              {!p.asClient ? <IconButton icon="eye" label="Preview as client" variant="ghost" size="sm" onClick={startPreview} /> : null}
              <IconButton icon="lock" label="Lock vault" variant="ghost" size="sm" onClick={() => v.lock()} />
            </div>
          </nav>
        ) : null}

        {!showSide || !mobile ? (
          <main className="main">
            {view === "vault" ? (
              vault ? (
                <VaultView vault={vault} />
              ) : (
                <div className="detail-empty">{loaded ? "Nothing to show yet." : <div className="spinner" aria-label="Loading" />}</div>
              )
            ) : view === "clients" ? (
              <ClientsView />
            ) : view === "expiring" ? (
              <ExpiringView />
            ) : view === "gen" ? (
              <GeneratorView />
            ) : view === "settings" ? (
              <SettingsView />
            ) : null}
          </main>
        ) : null}
      </div>
      {mobile ? (
        <div className="tabbar" role="tablist">
          {tabs.map(([label, icon, tv]) => {
            const on = tv === "home" ? nav.view === "home" || nav.view === "vault" : nav.view === tv;
            return (
              <button key={tv} type="button" role="tab" aria-selected={on} className={on ? "is-on" : ""} onClick={() => go({ view: tv, pane: "list" })}>
                <Icon name={icon} size={20} />
                <span>{label}</span>
              </button>
            );
          })}
        </div>
      ) : null}
      <Modals />
    </div>
  );
}
